import { mapState, mapActions } from 'vuex';
import { QueryString, SortOrder } from '@/Core/config/query-mappings';

class PaginationError extends Error {}

const DefaultPaginationSettings = {
  AllowedLimits: [5, 10, 25, 50],
  ExtraLimits: {
    contacts: [100, 150, 200, 500],
    tasks: [100, 200],
    'bulk-action-contacts': [100, 200],
  },
  StartingLimit: 10,
  StartPage: 1,
  SortOrder: 'asc',
};

export default {
  data() {
    return {
      paginationPage: DefaultPaginationSettings.StartPage,
      paginationPageLimit: DefaultPaginationSettings.StartingLimit,
      paginationSortOrder: null,
      paginationSortField: null,
      PaginationError,
    };
  },
  computed: {
    ...mapState({
      contactCommsPageSize: state => state.preferences.contactCommsPageSize,
      eventsPageSize: state => state.preferences.eventsPageSize,
      emailActivityPageSize: state => state.preferences.emailActivityPageSize,
      tasksPageSize: state => state.preferences.tasksPageSize,
      contactsTableItemsPersistance: state => state.preferences.contactsTableItemsPersistance,
      qualifyPageSize: state => state.preferences.qualifyPageSize,
      videoLibraryPageSize: state => state.preferences.videoLibraryPageSize,
    }),
    paginationGetSortField() {
      return this.$route.query[QueryString.SortField] || this.paginationSortField;
    },
    paginationGetSortOrder() {
      const { query } = this.$route;
      if (query[QueryString.SortOrder] && query[QueryString.SortField]) {
        return SortOrder[query[QueryString.SortOrder]];
      }
      return SortOrder[this.paginationSortOrder];
    },
    paginationGetPage() {
      return parseInt(this.$route.query.p, 10) || this.paginationPage;
    },
    paginationGetPageLimit() {
      return parseInt(this.$route.query.l, 10) || this.paginationPageLimit;
    },
    paginationLimits() {
      const { AllowedLimits, ExtraLimits } = DefaultPaginationSettings;
      return ExtraLimits[this.currentTable] ?
        [...AllowedLimits, ...ExtraLimits[this.currentTable]] :
        AllowedLimits;
    }
  },
  methods: {
    ...mapActions('preferences', ['updatePreference']),
    /**
     * updates page limit preferences--stored in localstorage
     */
    loadWithNewPageLimit(pageLimit, preferenceKey) {
      this.paginationPageLimit = pageLimit;
      this.paginationPage = 1;
      this.updatePreference({
        key: preferenceKey,
        value: pageLimit,
      });
    },
    /**
     * Updates the fields provided in newQuery on the current $route.query
     *
     * @param {Object} newQuery Query fields to be updated
     * @returns new $route.query
     */
    $_pagination_updateQueryString(newQuery) {
      return Object.assign({}, this.$route.query, newQuery);
    },

    /**
     * return the skip and limit dictated by the pagination
     * @return {Object}
     */
    paginationGetSkipAndLimit() {
      return {
        $skip: (this.paginationGetPage - 1) * this.paginationGetPageLimit,
        $limit: this.paginationGetPageLimit,
      };
    },

    paginationResetSkipAndLimit() {
      this.paginationLimits = DefaultPaginationSettings.AllowedLimits;
      this.paginationPage = DefaultPaginationSettings.StartPage;
      this.paginationPageLimit = DefaultPaginationSettings.StartingLimit;
      this.paginationSortOrder = null;
      this.paginationSortField = null;
    },

    /**
     * The pagination query string uses shorthands for
     * limit (l)
     * skip (p, for page)
     * Sort Field (sf)
     * Sort Order (so)
     */
    paginationRemoveShorthandKeys(query) {
      delete query[QueryString.SortField];
      delete query[QueryString.SortOrder];
      delete query[QueryString.Limit];
      delete query[QueryString.Page];
    },

    /**
     * Updates sort field and sort order and pushes updates query to Router
     * If sort field = current sort field we flip sort order
     *
     * @param {String} paginationSortField the field to sort on
     * @param {String} paginationSortOrderValue optional value, use to change the
     * default/paginationSortOrder.
     */
    paginationSetSort(paginationSortField, paginationSortOrderValue) {
      let query;
      if (this.paginationSortField === paginationSortField) {
        if (paginationSortOrderValue) {
          this.paginationSortOrder = paginationSortOrderValue;
        } else {
          this.paginationSortOrder = this.paginationSortOrder === 'desc' ? 'asc' : 'desc';
        }
        query = this.$_pagination_updateQueryString({
          [QueryString.SortField]: paginationSortField,
          [QueryString.SortOrder]: this.paginationSortOrder,
        });
      } else {
        this.paginationSortField = paginationSortField;

        query = this.$_pagination_updateQueryString({
          [QueryString.SortField]: paginationSortField,
          [QueryString.SortOrder]: paginationSortOrderValue || DefaultPaginationSettings.SortOrder,
        });
      }
      this.$router.push({ query });
    },

    /**
     * Calculates page count based off total records / items per page
     *
     * @param {Number} total
     * @param {Number} pageSize - could optionally provide a value from local storage
     * @returns Number
     */
    paginationPageCount(total, pageSize = this.paginationGetPageLimit) {
      return Math.ceil(total / pageSize) || 0;
    },

    /**
     * Updates items per page based off selected drop down option
     * Pushes new page and limit to route query
     *
     * @param {Number} limit
     */
    paginationSetPaginationLimit(limit) {
      this.paginationPageLimit = limit;
      this.paginationPage = 1;
      const query = this.$_pagination_updateQueryString({
        [QueryString.Limit]: limit,
        [QueryString.Page]: 1,
      });
      this.$router.push({ query });
    },

    /**
     * Pushes new page to route query on page selection
     *
     * @param {Number} paginationPage
     */
    paginationSetPage(paginationPage) {
      this.paginationPage = paginationPage;
      const query = this.$_pagination_updateQueryString({
        [QueryString.Page]: paginationPage,
      });
      this.$router.push({ query });
    },

    /**
     * Checks the special sorts object to see if the sortField maps to a single
     * field or to multiple fields
     *
     * ie. For campaign actions when sorting by 'schedule' we actually want to
     * sort by multiple fields on the backend, those being 'dispatch_datetime',
     * 'delay_by_days', 'dispatch_time', and 'delay_by_seconds'
     *
     * @param  {Object}  specialSorts Object that accounts for >1 column sorting
     * @param  {Object}  sortField    The sort field from the data table to check
     * @return {Boolean}              True/false if sorting on multiple columns
     */
    isSortingOnMultipleFields(specialSorts, sortField) {
      return specialSorts && Object.keys(specialSorts).includes(sortField);
    },

    /**
     * Set up the $sort object for query
     * this.specialSorts is set in parent component
     *
     * @param {Object} query
     * @returns {Object} $sort object of query
     */
    $_pagination_prepareSort(query) {
      const $sort = {};

      if (Object.keys(query).includes(QueryString.SortField)) {
        const sortOrder = query[QueryString.SortOrder] || this.paginationGetSortOrder;

        // TODO MW / MH - accept custom function for special sorts
        if (this.isSortingOnMultipleFields(this.specialSorts, query[QueryString.SortField])) {
          this.specialSorts[query[QueryString.SortField]].forEach(column => {
            $sort[column] = SortOrder[sortOrder];
          });
        } else {
          $sort[query[QueryString.SortField]] = SortOrder[sortOrder];
        }
      } else if (this.isSortingOnMultipleFields(this.specialSorts, this.paginationSortField)) {
        this.specialSorts[this.paginationSortField].forEach(column => {
          $sort[column] = SortOrder[this.paginationSortOrder];
        });
      } else {
        $sort[this.paginationSortField] = SortOrder[this.paginationSortOrder];
      }

      return $sort;
    },
    pagination_scrollToTop(tableRef) {
      const tableContainer = tableRef.$el.querySelector('.v-table__overflow');
      tableContainer.scrollTop = 0;
    },
  },
};
